import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { getTenantItem, setTenantItem } from '@/lib/tenantStorage';

interface SectionGroupPrefs {
  [key: string]: boolean;
}

const STORAGE_KEY_PREFIX = 'project-section-group-prefs';

/**
 * Returns auto-expand defaults based on workflow stage.
 * Groups not explicitly controlled default to false (collapsed) so the user
 * can see relevant sections without noise from other groups.
 */
export function getAutoExpandDefaults(workflowStage: string | undefined | null): SectionGroupPrefs {
  const stage = workflowStage ?? '';

  // Spec keystone-client-required-per-category (R8.5):
  // Project Management group should be open by default for any active non-proposal stage
  // so the Create Invoice button is visible at first scroll. User-collapsed state still
  // wins (stored prefs override these defaults).
  const proposalStages = ['proposal_draft', 'proposal_sent', 'proposal_declined'];
  const projectManagementDefault = !!stage && !proposalStages.includes(stage);

  const financialsStages = ['proposal_draft', 'proposal_sent'];
  const constructionAdminStages = ['construction_administration'];

  return {
    'group-project-management': projectManagementDefault,
    'group-financials': financialsStages.includes(stage),
    'group-construction-admin': constructionAdminStages.includes(stage),
  };
}

/**
 * Hook for managing project detail page section group collapse/expand preferences.
 * Persists user preferences to tenant-scoped, per-project localStorage.
 *
 * Auto-expand rules (applied only when no stored user preference exists for a group):
 * - Project Management: project_setup, design_development, permit_preparation, in_permit_review
 * - Financials: proposal_draft, proposal_sent
 * - Construction Admin: construction_administration
 */
export function useProjectSectionPreferences(
  projectId: string | undefined,
  workflowStage: string | undefined | null
) {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? '';

  const storageKey = projectId ? `${STORAGE_KEY_PREFIX}-${projectId}` : null;

  const [prefs, setPrefs] = useState<SectionGroupPrefs>(() => {
    const autoDefaults = getAutoExpandDefaults(workflowStage);

    if (!projectId || !tenantId || !storageKey) {
      return autoDefaults;
    }

    const stored = getTenantItem(storageKey, tenantId);
    if (stored) {
      try {
        const parsedStored = JSON.parse(stored);
        // Auto-expand defaults are the base; stored user preferences win
        return { ...autoDefaults, ...parsedStored };
      } catch {
        return autoDefaults;
      }
    }
    return autoDefaults;
  });

  // Re-apply auto-expand defaults when projectId or workflowStage change
  // (handles async data load: initially undefined, then populated)
  useEffect(() => {
    if (!projectId || !tenantId || !storageKey) return;

    const autoDefaults = getAutoExpandDefaults(workflowStage);
    const stored = getTenantItem(storageKey, tenantId);
    let parsedStored: SectionGroupPrefs = {};

    if (stored) {
      try {
        parsedStored = JSON.parse(stored);
      } catch {
        parsedStored = {};
      }
    }

    // Merge: auto-expand defaults first, stored user preferences override
    setPrefs({ ...autoDefaults, ...parsedStored });
  }, [projectId, workflowStage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist to tenant-scoped, per-project localStorage on change
  useEffect(() => {
    if (tenantId && storageKey) {
      setTenantItem(storageKey, JSON.stringify(prefs), tenantId);
    }
  }, [prefs, tenantId, storageKey]);

  const setGroupExpanded = (groupKey: string, isExpanded: boolean) => {
    setPrefs((prev) => ({
      ...prev,
      [groupKey]: isExpanded,
    }));
  };

  const isGroupExpanded = (groupKey: string): boolean => {
    if (groupKey in prefs) {
      return prefs[groupKey];
    }
    // Unknown groups default to expanded
    return true;
  };

  return {
    isGroupExpanded,
    setGroupExpanded,
  };
}
